// Services list. Members see a read-only list; admins additionally see
// the create form and per-row links to the detail/edit page.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { onSnapshot, query, orderBy } from 'firebase/firestore';
import { Plus, Server, ArrowRight } from 'lucide-react';
import { useOrg } from '../contexts/OrgContext';
import { orgServicesCol } from '../lib/firestore';
import {
  STATUS_LABELS,
  STATUS_TONES,
  TIER_LABELS,
  TIER_TONES,
  createService,
} from '../lib/services';
import type { Service, ServiceTier } from '../lib/types';
import {
  AppShell,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  FormField,
  Input,
  PageHeader,
  Row,
  Spinner,
  Stack,
  Table,
} from '../components/ui';
import { ErrorBanner } from './SigninPage';

export default function ServicesPage() {
  const { orgId, role } = useOrg();
  const isAdmin = role === 'admin';

  const [services, setServices] = useState<Service[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Inline create form (admins only).
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newTier, setNewTier] = useState<ServiceTier>('tier3');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    const unsub = onSnapshot(
      query(orgServicesCol(orgId), orderBy('name', 'asc')),
      (snap) => {
        setServices(snap.docs.map((d) => d.data() as Service));
      },
      (err) => {
        console.error('[services] snapshot failed', err);
        setError('Could not load services.');
      },
    );
    return () => unsub();
  }, [orgId]);

  if (!orgId) return null;

  async function submitCreate(e: React.FormEvent) {
    e.preventDefault();
    if (creating) return;
    setCreating(true);
    setError(null);
    try {
      await createService(orgId!, {
        name: newName.trim(),
        tier: newTier,
        description: newDescription.trim() || undefined,
      });
      setNewName('');
      setNewDescription('');
      setNewTier('tier3');
      setShowForm(false);
    } catch (err) {
      console.error('[services] create failed', err);
      setError(extractMessage(err));
    } finally {
      setCreating(false);
    }
  }

  return (
    <AppShell>
      <Container width="wide">
        <PageHeader
          title="Services"
          subtitle="Define monitored services. Each one will get an escalation policy and integrations as those chunks ship."
          actions={
            isAdmin && !showForm ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowForm(true)}
                iconLeft={<Plus size={14} strokeWidth={2} />}
              >
                Add service
              </Button>
            ) : null
          }
        />

        {error && (
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <ErrorBanner message={error} />
          </div>
        )}

        <Stack gap={6}>
          {showForm && isAdmin && (
            <Card>
              <CardHeader>
                <Row gap={2} align="center">
                  <Server size={16} strokeWidth={1.75} color="var(--color-accent)" />
                  <h2 style={{ fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)' }}>
                    New service
                  </h2>
                </Row>
              </CardHeader>
              <CardBody>
                <Stack gap={4} as="form" onSubmit={submitCreate}>
                  <FormField label="Name">
                    {(id) => (
                      <Input
                        id={id}
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="API Gateway"
                        required
                        maxLength={80}
                        autoFocus
                      />
                    )}
                  </FormField>
                  <FormField
                    label="Tier"
                    hint="Tier 1 is highest priority. Tier mapping informs incident priority later."
                  >
                    {(id) => (
                      <select
                        id={id}
                        className="ds-input"
                        value={newTier}
                        onChange={(e) => setNewTier(e.target.value as ServiceTier)}
                      >
                        <option value="tier1">Tier 1 — Critical</option>
                        <option value="tier2">Tier 2 — Major</option>
                        <option value="tier3">Tier 3 — Minor</option>
                      </select>
                    )}
                  </FormField>
                  <FormField label="Description" hint="Optional">
                    {(id) => (
                      <textarea
                        id={id}
                        className="ds-input"
                        value={newDescription}
                        onChange={(e) => setNewDescription(e.target.value)}
                        maxLength={500}
                        placeholder="What this service does."
                        rows={3}
                      />
                    )}
                  </FormField>
                  <Row gap={2} justify="end">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setShowForm(false)}
                      disabled={creating}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={!newName.trim()}
                      loading={creating}
                    >
                      Create service
                    </Button>
                  </Row>
                </Stack>
              </CardBody>
            </Card>
          )}

          {services === null ? (
            <Row gap={2} align="center">
              <Spinner size="sm" />
              <span className="ds-text-muted ds-text-sm">Loading…</span>
            </Row>
          ) : services.length === 0 ? (
            <Card>
              <CardBody>
                <Stack gap={3}>
                  <p className="ds-text-muted">
                    No services yet.{' '}
                    {isAdmin
                      ? 'Add your first one above.'
                      : 'An admin needs to add the services your org monitors.'}
                  </p>
                </Stack>
              </CardBody>
            </Card>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Tier</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>{isAdmin ? 'Actions' : ''}</th>
                </tr>
              </thead>
              <tbody>
                {services.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <Stack gap={1}>
                        <span style={{ fontWeight: 'var(--fw-medium)' }}>{s.name}</span>
                        {s.description && (
                          <span className="ds-text-muted ds-text-xs">{s.description}</span>
                        )}
                      </Stack>
                    </td>
                    <td>
                      <Badge tone={TIER_TONES[s.tier]}>{TIER_LABELS[s.tier]}</Badge>
                    </td>
                    <td>
                      <Badge tone={STATUS_TONES[s.currentStatus]} dot>
                        {STATUS_LABELS[s.currentStatus]}
                      </Badge>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Link
                        to={`/services/${s.id}`}
                        className="ds-button ds-button--ghost ds-button--sm"
                      >
                        {isAdmin ? 'Manage' : 'View'}
                        <ArrowRight size={12} strokeWidth={2} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Stack>
      </Container>
    </AppShell>
  );
}

function extractMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return 'Something went wrong. Try again.';
}
